import React from 'react';
import { Col, Row, Skeleton, Statistic, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { METRIC_LABELS, WINDOWED_EARNINGS_CAPTION, WINDOWED_EARNINGS_TOOLTIP } from '../constants';
import { formatCount, formatCurrency, formatPercentage } from '../utils/formatters';
import { SectionCard, SectionTitle, StatCard, StatsRow, WindowedCaption } from '../Styles';
import type { PerformanceSummary } from '../types';

interface SummaryCardsProps {
  summary?: PerformanceSummary;
  loading?: boolean;
}

/**
 * The §3.2 summary metrics, minus the customer mix (which gets its own card).
 *
 * Two things the docs call out explicitly and this component honours:
 * - `netCustomerDiscount` / `netCreatorEarnings` are shown, NOT the raw
 *   `customerDiscount` / `creatorEarnings`. The net figures back out refunds and are
 *   the ones that answer "what did this actually cost".
 * - `earnings.*` are window-scoped, not a live balance, so they're segregated into
 *   their own captioned section instead of sitting alongside the totals.
 */
const SummaryCards: React.FC<SummaryCardsProps> = ({ summary, loading }) => {
  if (loading) {
    return (
      <SectionCard>
        <Skeleton active paragraph={{ rows: 4 }} />
      </SectionCard>
    );
  }

  // A creator with no orders yet returns a zeroed summary rather than nothing, but
  // guard every read anyway — `?? 0` keeps the cards rendering instead of blanking.
  const orders = summary?.orders ?? 0;
  const refundCount = summary?.refunds?.count ?? 0;

  return (
    <>
      <StatsRow>
        <StatCard>
          <Statistic title={METRIC_LABELS.ORDERS} value={formatCount(orders)} />
        </StatCard>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.GROSS_SUBTOTAL}
            value={formatCurrency(summary?.grossSubtotal)}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.COMMISSION_BASE}
            value={formatCurrency(summary?.commissionBase)}
          />
        </StatCard>
        <StatCard>
          <Statistic title={METRIC_LABELS.AOV} value={formatCurrency(summary?.aov)} />
        </StatCard>
      </StatsRow>

      <StatsRow>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.NET_CUSTOMER_DISCOUNT}
            value={formatCurrency(summary?.netCustomerDiscount)}
            styles={{ content: { color: '#d46b08' } }}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.NET_CREATOR_EARNINGS}
            value={formatCurrency(summary?.netCreatorEarnings)}
            styles={{ content: { color: '#2D6CCA' } }}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.TOTAL_COST}
            value={formatCurrency(summary?.totalCost)}
            styles={{ content: { color: '#cf1322' } }}
          />
        </StatCard>
        <StatCard>
          <Statistic
            title={METRIC_LABELS.COST_PERCENT_OF_BASE}
            value={formatPercentage(summary?.costPercentOfBase)}
          />
        </StatCard>
      </StatsRow>

      <SectionCard>
        <SectionTitle>Refunds</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
          <Col xs={12} md={6}>
            <Statistic
              title={METRIC_LABELS.REFUND_COUNT}
              value={formatCount(refundCount)}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title={METRIC_LABELS.REFUND_SUBTOTAL}
              value={formatCurrency(summary?.refunds?.subtotal)}
            />
          </Col>
        </Row>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          Earnings by status{' '}
          <Tooltip title={WINDOWED_EARNINGS_TOOLTIP}>
            <InfoCircleOutlined style={{ fontSize: 14, opacity: 0.65 }} />
          </Tooltip>
        </SectionTitle>
        <WindowedCaption>{WINDOWED_EARNINGS_CAPTION}</WindowedCaption>

        <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
          <Col xs={12} md={6}>
            <Statistic
              title={METRIC_LABELS.EARNINGS_PENDING}
              value={formatCurrency(summary?.earnings?.pending)}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title={METRIC_LABELS.EARNINGS_PAYABLE}
              value={formatCurrency(summary?.earnings?.payable)}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title={METRIC_LABELS.EARNINGS_PAID}
              value={formatCurrency(summary?.earnings?.paid)}
            />
          </Col>
        </Row>
      </SectionCard>
    </>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(SummaryCards);
